import { ShareAltOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import oddsApi from 'api/oddsApi';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import InfomationIcon from 'assets/icons/InfomationIcon';
import BigNumber from 'bignumber.js';
import { showConfirmModal, showSuccessModal } from 'components/Modals/CommonModals';
import Typo from 'consts/typography';
import { selectCurrentConverter } from 'features/home/homeSlice';
import ShareSingleBetTwitter from 'features/twitter/ShareSingleBetTwiiter';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { BetResponseModel } from 'models';
import React, { useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { getMarketBetValue, getMarketName, statusColor } from 'utils/betUtils';
import { isPhone, isTablet } from 'utils/commonUtils';
import { getDateWithFormat24h } from './MyBetSingle';

interface MybetSingleItemProps {
  bet: BetResponseModel;
  tab: number;
}

const getBigDecimalPossibleWin = (amount: number, decimalPrice: number) => {
  const amountBigNumber = new BigNumber(amount);
  return amountBigNumber.times(new BigNumber(decimalPrice)).minus(amountBigNumber);
};

const MybetSingleItem: React.FunctionComponent<MybetSingleItemProps> = ({ tab, bet }) => {
  const t = useAppTranslation();
  const platform = useAppSelector(platformStates);
  const { isDesktop, isLargeDesktop } = platform;
  const handleResponseError = useHandleResponseError();
  const [isShowShare, setIsShowShare] = useState<boolean>(false);
  const converter = useAppSelector(selectCurrentConverter);
  const colStyle1 = `col-span-3 primary-text-color m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
  } font-medium`;

  const colStyle2 = ` col-span-2 m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
  } font-normal`;
  const handleShareBet = async () => {
    const onOk = async () => {
      const { ok, error } = await oddsApi.shareBets({
        betId: bet.id,
      });
      if (ok) {
        showSuccessModal({
          title: t('sharebet.success.title'),
          content: t('sharebet.success.message'),
        });
      }
      if (error) {
        handleResponseError(error);
      }
    };

    showConfirmModal({
      content: <p>{t('sharebet.popup.sharebet-content')}</p>,
      width: 360,
      icon: <InfomationIcon />,
      onOk: onOk,
      okText: t('sharebet.popup.button-ok'),
      cancelText: t('sharebet.popup.button-cancel'),
    });
  };

  const betAmount = useMemo(() => {
    return `${bet.amount} $`;
  }, [bet.id]);

  const winBetAmount = useMemo(() => {
    const winAmount =
      tab === 1
        ? getBigDecimalPossibleWin(
            bet.amount,
            bet?.betDetails[0].type === 'BET_GAME'
              ? bet.betDetails[0].market.decimalPrice
              : bet.betDetails[0].futureMarket?.decimalPrice,
          )
        : new BigNumber(bet.winAmount);

    return `${winAmount} $`;
  }, [bet.id]);

  const statusClassname = (betStatus: string) => {
    const className = statusColor[betStatus] || 'bet-cancel';
    return `col-span-2 m-0 ${
      isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
    } font-bold ${className}`;
  };

  const showSelectionTeam = () => {
    if (bet?.betDetails[0]?.type !== 'BET_GAME') return null;

    const detail = bet.betDetails[0];
    const { market, game } = detail;
    const selectedTeam = market?.selection;
    const { homeTeam, awayTeam } = game;
    const marketName = getMarketName(bet.betDetails[0]?.market?.market);
    const isHomeSelected = selectedTeam === homeTeam || market.selectionLine === 'Player 1';
    const betValue = getMarketBetValue(
      market?.market,
      converter,
      market?.price,
      market?.decimalPrice,
      market?.betPoints || 0,
      market?.selectionLine || '',
    );
    if (marketName === 'Total point') {
      return (
        <span className={`selection__team ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7}`}>
          {homeTeam} vs {awayTeam}
        </span>
      );
    } else if (
      marketName === 'First half total' ||
      marketName === 'Second half total' ||
      marketName === 'Odd_Even'
    ) {
      return (
        <span className={`selection__team ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7}`}>
          {homeTeam} vs {awayTeam}
          <span className={`${colStyle2} number-bet`}> {`${betValue}`}</span>
        </span>
      );
    } else {
      if (game.sport.group === 'Soccer' && selectedTeam === 'Draw') {
        return (
          <span className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7}`}>
            {homeTeam} vs {awayTeam} <span className="selection__team">Draw</span>
          </span>
        );
      } else {
        return (
          <span className={`${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7}`}>
            {isHomeSelected ? (
              <>
                <span className="selection__team">{homeTeam}</span> vs {awayTeam}
              </>
            ) : (
              <>
                {homeTeam} vs <span className="selection__team">{awayTeam}</span>
              </>
            )}{' '}
            <span className={`${colStyle2} number-bet`}>{`${betValue}`}</span>
          </span>
        );
      }
    }
  };

  const showHeader = () => {
    const detail = bet.betDetails[0];
    const { market, type } = detail;
    const betValue = getMarketBetValue(
      market?.market,
      converter,
      market?.price,
      market?.decimalPrice,
      market?.betPoints || 0,
      market?.selectionLine || '',
    );
    const marketName = getMarketName(bet.betDetails[0]?.market?.market);
    if (type === 'BET_GAME') {
      if (marketName === 'Total point') {
        const displayValue = betValue
          ?.toString()
          .replace(/^O\s?/, 'Over ')
          .replace(/^U\s?/, 'Under ');

        return (
          <p
            className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7} m-0`}
          >
            {`${t('myBet.singleBet')}: ${displayValue}`}
          </p>
        );
      } else {
        return (
          <p
            className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7} m-0 `}
          >
            {`${t('myBet.singleBet')}/${getMarketName(bet.betDetails[0]?.market?.market)}`}
          </p>
        );
      }
    } else {
      return (
        <p className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7} m-0 `}>
          {`${t('myBet.singleBet')}/Future`}
        </p>
      );
    }
  };

  return (
    <div className="my-bet">
      <div className="my-bet__header primary-text-color">
        <Row className="my-bet__title">
          <Col flex={4}>
            <div className="body">
              {showHeader()}
              <div className="my-bet_information">
                <div className="result">
                  {bet?.betDetails[0].type === 'BET_GAME' ? (
                    showSelectionTeam()
                  ) : (
                    <span
                      className={`selection__team ${
                        isDesktop || isLargeDesktop ? Typo.p3 : Typo.p7
                      }`}
                    >
                      {bet?.betDetails[0].futureOdds?.name}{' '}
                      <span className={`${colStyle2} number-bet`}>
                        {bet.betDetails[0]?.futureOdds?.decimalPrice}
                      </span>
                    </span>
                  )}
                  <span className={`${colStyle1} font-bold`}>{`${t(
                    'myBet.betAmount',
                  )} ${betAmount}`}</span>
                  <span>
                    {tab === 2 ? (
                      <div className="result">
                        <p className={statusClassname(bet.status)}>{`${bet.status}: ${
                          tab === 2 && bet.status === 'WIN' ? winBetAmount : betAmount
                        }`}</p>
                      </div>
                    ) : (
                      <p className={`${colStyle1} font-bold`}>
                        {t('myBet.possibleWin')}
                        {': '}
                        {winBetAmount}
                      </p>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          <Col>
            <Button
              shape="round"
              icon={<ShareAltOutlined />}
              size="small"
              className="share-bet__btn"
              onClick={handleShareBet}
            />
          </Col>
        </Row>
        <div className="data">
          <div className="data__left-side">
            <p
              className={`primary-text-color ${
                isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
              } font-bold`}
            >
              ID: {bet?.id}
            </p>
            {/* {bet?.betDetails[0]?.type !== 'BET_GAME' && (
              <>
                <p className={`${colStyle2} number-bet`}>
                  {bet.betDetails[0]?.futureOdds?.decimalPrice}
                </p>
              </>
            )} */}
          </div>
          <p
            className={`primary-text-color ${
              isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p6
            } font-bold`}
          >
            PLACED: {getDateWithFormat24h(bet.createdDate)}
          </p>
        </div>
      </div>
      <ShareSingleBetTwitter
        visible={isShowShare}
        closeModal={() => setIsShowShare(false)}
        bet={bet}
        tab={tab}
        className="common-modal withdraw-modal"
      />
    </div>
  );
};

export default MybetSingleItem;
